/**
 * Product analytics — batched event tracker.
 *
 * Usage
 * -----
 *   import { trackEvent, initAnalytics } from '@/lib/analytics';
 *   trackEvent('map_opened', { country: 'fr' });
 *
 * Design
 * ------
 * • All events buffer locally and flush every 8s OR when the buffer reaches
 *   20 events OR when the tab goes hidden / unloads (sendBeacon). This keeps
 *   network chatter low and works even for the very last click before a
 *   user closes the tab (which is exactly the drop-off signal we want!).
 * • A stable session id is stored in sessionStorage so events from the
 *   same tab session are grouped, without following the user across
 *   incognito boundaries (GDPR-friendly).
 * • Only whitelisted events are accepted on the backend (see
 *   /app/backend/events_service.py). Anything else is silently dropped.
 * • Auth token is picked up from localStorage on each flush so anonymous
 *   -> signed-in transitions are correctly attributed.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TRACK_PATH = '/api/events/track';
const FLUSH_INTERVAL_MS = 8000;
const FLUSH_MAX_BUFFER = 20;
const SESSION_KEY = 'dm_session_id';
const TOKEN_KEY = 'dronemaps_token';

let buffer = [];
let flushTimer = null;
let initialised = false;

function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null; // e.g. Safari private mode
  }
}

function getAuthHeader() {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

async function flush({ useBeacon = false } = {}) {
  if (buffer.length === 0 || !API_URL) return;
  const batch = buffer.splice(0, buffer.length);
  const payload = {
    session_id: getSessionId(),
    events: batch,
  };
  const url = `${API_URL}${TRACK_PATH}`;

  // On unload, `sendBeacon` is the only reliable way to ship data without
  // being cancelled by the browser. It can't carry Authorization headers,
  // so anon-only in that path — acceptable for a final "abandoned" event.
  if (useBeacon && navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } catch { /* swallow */ }
    return;
  }

  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Silent — analytics must never break UX. Events are lost on failure
    // rather than retried in memory to avoid unbounded growth.
  }
}

function schedule() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

export function trackEvent(event, props = {}) {
  if (!event || typeof event !== 'string') return;
  buffer.push({
    event,
    path: (typeof window !== 'undefined' ? window.location.pathname : null),
    props,
    ts: new Date().toISOString(),
  });
  if (buffer.length >= FLUSH_MAX_BUFFER) {
    // Cancel the pending timer — we're flushing now.
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    flush();
  } else {
    schedule();
  }
}

export function initAnalytics() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;
  // Ensure the session id is created up-front (so anonymous page_views
  // during signup share the same session as the resulting signed-in events).
  getSessionId();
  // Flush on tab hidden / unload — best chance to capture the final
  // interaction (which is often the "why did they leave?" signal).
  const finalFlush = () => flush({ useBeacon: true });
  window.addEventListener('pagehide', finalFlush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') finalFlush();
  });
}

export default { trackEvent, initAnalytics };
